import { useId } from 'react';
import { Button } from '../../../components/Button';
import type { AdminTourPlatformKey } from '../../../types/adminTours';

/** What the editor is waiting on. `reload` is a refetch of the tour already on screen. */
export type TourEditorBusy = 'save' | 'discard' | 'publish' | 'reset' | 'pick' | 'reload';

interface TourEditorActionsProps {
  platform: AdminTourPlatformKey;
  /** The web app URL that runs this tour as a preview, or null when the tour has no web route. */
  previewHref: string | null;
  hasDraft: boolean;
  /**
   * False when the screen matches the last write, which makes Save a no-op that creates a draft.
   * True blocks Publish: it publishes the stored draft, so unsaved edits would not go out, and the
   * response would replace them on screen.
   */
  dirty: boolean;
  busy: TourEditorBusy | null;
  onSave: () => void;
  onDiscard: () => void;
  onPublish: () => void;
  onReshow: () => void;
}

/**
 * The four writes and the preview.
 *
 * Preview is a link rather than a button because it goes to another page in a new tab, which is a
 * navigation: a button would lose the middle-click and the context menu for no gain. Mobile's
 * preview is not a link at all - it is a switch inside the app - so the tab says where to find it.
 */
export const TourEditorActions = ({
  platform,
  previewHref,
  hasDraft,
  dirty,
  busy,
  onSave,
  onDiscard,
  onPublish,
  onReshow,
}: TourEditorActionsProps) => {
  const working = busy !== null;
  const publishReasonId = useId();

  return (
    <div className="tour-editor__actions">
      <Button
        type="button"
        onClick={onSave}
        disabled={working || !dirty}
        loading={busy === 'save'}
        data-testid="tour-save-draft"
      >
        Save draft
      </Button>
      <Button
        type="button"
        variant="secondary"
        onClick={onDiscard}
        disabled={working || !hasDraft}
        data-testid="tour-discard-draft"
      >
        Discard draft
      </Button>

      {platform === 'web' ? (
        previewHref ? (
          <a
            className="tour-editor__link-button"
            href={previewHref}
            target="_blank"
            rel="noreferrer"
            data-testid="tour-preview-link"
          >
            Preview on the web app
          </a>
        ) : (
          <span className="tour-editor__hint">This tour has no web route to preview.</span>
        )
      ) : (
        <span className="tour-editor__hint" data-testid="tour-preview-mobile-hint">
          To preview on mobile, turn on Preview tour drafts on the profile tab in the app.
        </span>
      )}

      <Button
        type="button"
        onClick={onPublish}
        disabled={working || dirty}
        loading={busy === 'publish'}
        aria-describedby={dirty ? publishReasonId : undefined}
        data-testid="tour-publish"
      >
        Publish
      </Button>
      {/* A disabled button says nothing about why, so the reason is on screen and wired to it. */}
      {dirty && (
        <span
          id={publishReasonId}
          className="tour-editor__hint"
          data-testid="tour-publish-blocked-reason"
        >
          Save the draft first. Publish sends the saved draft, not changes still on this screen.
        </span>
      )}
      <Button
        type="button"
        variant="secondary"
        onClick={onReshow}
        disabled={working}
        data-testid="tour-reshow"
      >
        Re-show to all users
      </Button>
    </div>
  );
};
